import { useTheme } from '../../context/theme';
import { renderIcon } from '../../utils/icons';
import { N, R, pC, sC, sI, COLORS } from '../../utils/constants';

export function Btn({ children, onClick, primary, sm, dis, full, danger, style: st }) {
  const { theme } = useTheme();
  return (
    <button
      className={dis ? '' : 'action-btn' + (primary ? ' glow-btn' : '')}
      onClick={onClick}
      disabled={dis}
      style={{
        padding: sm ? '8px 16px' : '11px 22px',
        background: danger ? 'rgba(239,68,68,0.10)' : primary ? theme.accent : theme.bgCard,
        border: danger ? '1px solid rgba(239,68,68,0.20)' : primary ? '1px solid rgba(74,222,128,0.3)' : `1px solid ${theme.border}`,
        borderRadius: 8,
        color: danger ? '#EF4444' : primary ? '#0A0A0A' : theme.text,
        cursor: dis ? 'not-allowed' : 'pointer',
        fontSize: sm ? 13 : 14,
        fontWeight: 600,
        fontFamily: "'DM Sans', sans-serif",
        letterSpacing: '0.01em',
        opacity: dis ? 0.4 : 1,
        width: full ? '100%' : 'auto',
        ...(st || {}),
      }}
    >
      {children}
    </button>
  );
}

export function Inp({ ...p }) {
  const { theme } = useTheme();
  return (
    <input
      {...p}
      style={{
        width: '100%', padding: '11px 14px',
        background: theme.bgInput, border: `1px solid ${theme.border}`,
        borderRadius: 8, color: theme.text, fontSize: 14, outline: 'none',
        fontFamily: "'DM Sans', sans-serif",
        transition: 'border-color 150ms cubic-bezier(0.16,1,0.3,1), box-shadow 200ms cubic-bezier(0.16,1,0.3,1)',
        ...(p.style || {}),
      }}
    />
  );
}

export function Sel({ children, ...p }) {
  const { theme } = useTheme();
  return (
    <div style={{ position: 'relative', display: 'inline-block', width: p.style?.width || '100%' }}>
      <select
        {...p}
        style={{
          width: '100%', padding: '9px 32px 9px 12px',
          background: theme.bgCard, border: `1px solid ${theme.border}`,
          borderRadius: 8, color: theme.textSecondary, fontSize: 14,
          fontFamily: "'DM Sans', sans-serif",
          cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none',
          transition: 'border-color 150ms cubic-bezier(0.16,1,0.3,1)',
          ...(p.style || {}),
        }}
      >
        {children}
      </select>
      <span style={{
        position: 'absolute', right: 10, top: '50%',
        transform: 'translateY(-50%)', color: theme.textMuted,
        fontSize: 10, pointerEvents: 'none',
      }}>▼</span>
    </div>
  );
}

export function Card({ children, style: s, onClick, id }) {
  const { theme } = useTheme();
  return (
    <div
      id={id}
      onClick={onClick}
      className="hover-card"
      style={{
        background: theme.bgCard, border: `1px solid ${theme.border}`,
        borderRadius: 14, padding: 22, boxShadow: theme.shadow,
        ...(s || {}),
      }}
    >
      {children}
    </div>
  );
}

export function EmptyState({ icon, title, description, action, onAction }) {
  const { theme } = useTheme();
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: '60px 24px', textAlign: 'center',
      background: 'radial-gradient(ellipse at center, rgba(255,255,255,0.02) 0%, transparent 70%)',
      borderRadius: 16, minHeight: 200,
    }}>
      {icon && <div style={{ marginBottom: 16, lineHeight: 1, opacity: 0.6 }}>{renderIcon(icon, 48, theme.textMuted)}</div>}
      <div style={{ fontSize: 20, fontFamily: "'DM Sans', sans-serif", fontWeight: 400, color: theme.text, marginBottom: 8 }}>{title}</div>
      <div style={{ fontSize: 14, color: theme.textMuted, maxWidth: 360, lineHeight: 1.6, marginBottom: action ? 24 : 0, fontFamily: "'DM Sans', sans-serif" }}>{description}</div>
      {action && onAction && <Btn primary sm onClick={onAction}>{action}</Btn>}
    </div>
  );
}

export function Avatar({ name, color, sz = 32 }) {
  return (
    <div style={{
      width: sz, height: sz, borderRadius: sz > 36 ? 12 : 8,
      background: color || '#3E3E44', display: 'flex', alignItems: 'center',
      justifyContent: 'center', color: '#FFFFFF', fontWeight: 600,
      fontSize: sz * 0.38, flexShrink: 0,
      fontFamily: "'DM Sans', sans-serif",
      letterSpacing: '0.02em',
    }}>
      {name?.[0]?.toUpperCase() || '?'}
    </div>
  );
}

export function Logo({ sz = 36, wide }) {
  return (
    <img
      src="/logo.png"
      alt="Lightning Partners"
      style={{
        height: wide ? 'auto' : sz, width: wide ? '100%' : 'auto', flexShrink: 0,
        objectFit: 'contain', filter: 'brightness(1)',
      }}
    />
  );
}

export function PP({ p }) {
  const desc = {
    Critical: 'Immediate action needed — blocks progress',
    High: 'Should be addressed soon',
    Medium: 'Standard priority',
    Low: 'Nice to have, not urgent',
    Conditional: 'Depends on other factors',
  };
  const color = pC[p] || 'rgba(255,255,255,0.42)';
  const isCritical = p === 'Critical';
  return (
    <span
      title={desc[p] || p}
      style={{
        padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
        fontFamily: "'DM Sans', sans-serif",
        letterSpacing: '0.02em',
        background: color + '22', color: color,
        border: `1px solid ${color}35`,
        whiteSpace: 'nowrap', cursor: 'help',
        boxShadow: isCritical ? `0 0 8px ${color}20` : 'none',
      }}
    >
      {p}
    </span>
  );
}

export function SD({ s }) {
  const color = sC[s] || 'rgba(255,255,255,0.42)';
  const isBlocked = s === 'Blocked';
  const isComplete = s === 'Complete';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13,
      color: color, fontFamily: "'DM Sans', sans-serif", fontWeight: 500,
      textShadow: (isBlocked || isComplete) ? `0 0 10px ${color}40` : 'none',
    }}>
      {renderIcon(sI[s] || 'circle', 15, color)}
      <span className="hide-mobile">{s}</span>
    </span>
  );
}

export function PR({ pct, sz = 56, sk = 4 }) {
  const { theme, isDark } = useTheme();
  const r = (sz - sk) / 2;
  const c = 2 * Math.PI * r;
  const strokeColor = pct === 0 ? (isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)') : pct >= 50 ? '#4ADE80' : (isDark ? 'rgba(255,255,255,0.78)' : 'rgba(0,0,0,0.55)');
  const bgStroke = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)';
  const glowFilter = pct >= 100 ? 'drop-shadow(0 0 4px rgba(80,232,144,0.4))' : 'none';
  return (
    <svg width={sz} height={sz} style={{ transform: 'rotate(-90deg)', filter: glowFilter }}>
      <circle cx={sz / 2} cy={sz / 2} r={r} fill="none" stroke={bgStroke} strokeWidth={sk} />
      <circle cx={sz / 2} cy={sz / 2} r={r} fill="none" stroke={strokeColor} strokeWidth={sk}
        strokeDasharray={c} strokeDashoffset={c - (pct / 100) * c} strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 600ms cubic-bezier(0.16,1,0.3,1)' }} />
      <text x={sz / 2} y={sz / 2} textAnchor="middle" dy=".35em" fill={theme.text}
        fontSize={sz > 50 ? 13 : 10} fontWeight={600}
        fontFamily="'DM Sans', sans-serif"
        style={{ transform: 'rotate(90deg)', transformOrigin: 'center' }}>
        {pct}%
      </text>
    </svg>
  );
}

export { N, R, pC, sC, sI, COLORS };
